import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { forceCollide } from 'd3-force-3d';
import { Icon } from './ui';
import {
  getDiscoveryGraph,
  renameDiscoveredAgent,
  type DiscoveryGraph,
  type GraphFilters,
  type GraphNode,
  type NodeType,
  type RiskLevel,
} from '../lib/graphApi';
import { useTranslation } from 'react-i18next';

interface Props {
  apiUrl: string;
}

const PANEL_WIDTH = 320;

// Time-range presets — discrete stops so the query maps cleanly to a single
// `since` cutoff. "All time" omits the since filter entirely.
const TIME_RANGE_PRESETS: { labelKey: string; ms: number | null }[] = [
  { labelKey: 'components.shadowAiGraph.lastHour', ms: 60 * 60 * 1000 },
  { labelKey: 'components.shadowAiGraph.last6Hours', ms: 6 * 60 * 60 * 1000 },
  { labelKey: 'components.shadowAiGraph.last24Hours', ms: 24 * 60 * 60 * 1000 },
  { labelKey: 'components.shadowAiGraph.last7Days', ms: 7 * 24 * 60 * 60 * 1000 },
  { labelKey: 'components.shadowAiGraph.last30Days', ms: 30 * 24 * 60 * 60 * 1000 },
  { labelKey: 'components.shadowAiGraph.last90Days', ms: 90 * 24 * 60 * 60 * 1000 },
  { labelKey: 'components.shadowAiGraph.allTime', ms: null },
];

// Fixed palette (canvas can't read CSS vars). Chosen to read on light + dark.
const RISK_COLOR: Record<RiskLevel, string> = {
  sanctioned: '#22c55e',
  tolerated: '#f59e0b',
  unsanctioned: '#ef4444',
  high_risk: '#dc2626',
};
const TYPE_COLOR: Record<NodeType, string> = {
  identity: '#8b5cf6', // violet
  agent: '#3b82f6', // blue
  destination: '#94a3b8', // slate (overridden by risk when known)
  mcp: '#14b8a6', // teal
};

const RISK_LABEL: Record<string, string> = {
  sanctioned: 'Sanctioned',
  tolerated: 'Tolerated',
  unsanctioned: 'Unsanctioned',
  high_risk: 'High Risk',
};
const TYPE_LABEL: Record<NodeType, string> = {
  identity: 'Identity',
  agent: 'Agent',
  destination: 'AI Destination',
  mcp: 'MCP Server',
};

/** Same semantic icon each node type uses elsewhere on the Discovery page. */
const TYPE_ICON: Record<NodeType, 'users' | 'chip' | 'bolt' | 'server'> = {
  identity: 'users',
  agent: 'chip',
  destination: 'bolt',
  mcp: 'server',
};

const TYPE_HINT: Record<NodeType, string> = {
  identity: 'The user, host, or IP behind an agent — who or what is actually driving the traffic.',
  agent: 'A discovered AI agent or workload observed talking to a model or tool.',
  destination: 'An external AI endpoint (model API) an agent was seen calling.',
  mcp: 'An MCP tool server an agent was seen calling for tool access.',
};

/** Risk ordering used to sort the destination column — worst first, top-left. */
const RISK_RANK: Record<string, number> = { high_risk: 0, unsanctioned: 1, tolerated: 2, sanctioned: 3 };

/** Effective node color: destinations/mcp colored by risk when known, else by type. */
function nodeColor(n: GraphNode): string {
  if ((n.type === 'destination' || n.type === 'mcp') && n.risk) return RISK_COLOR[n.risk];
  return TYPE_COLOR[n.type];
}

function nodeRadius(n: GraphNode): number {
  return 7 + Math.min(9, Math.sqrt(Math.max(n.count, 0)) * 1.3);
}

/**
 * Short, human-readable label for the canvas.
 *  - destinations arrive as "OpenAI (api.openai.com)" — the provider is already
 *    shown as a monogram badge, so the host alone is the useful part;
 *  - identities are often raw SIDs, which are far too long to render — those get
 *    middle-truncated (the full value is always in the tooltip + detail panel).
 */
function shortLabel(n: GraphNode): string {
  let s = n.label || n.id;
  // Greedy head + innermost trailing group, so "ChatGPT (desktop) (chatgpt.com)"
  // yields "chatgpt.com" rather than "desktop) (chatgpt.com".
  const paren = /^(.*)\s+\(([^()]*)\)$/.exec(s);
  if (paren && (n.type === 'destination' || n.type === 'mcp')) s = paren[2];
  if (s.length > 26) s = `${s.slice(0, 14)}…${s.slice(-9)}`;
  return s;
}

// ── Color helpers (canvas works in raw hex/rgba, not CSS vars) ───────────────
function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return [148, 163, 184];
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}
function rgba(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}
function lighten(hex: string, amt: number): string {
  const [r, g, b] = hexToRgb(hex);
  const mix = (c: number) => Math.round(c + (255 - c) * amt);
  return `rgb(${mix(r)},${mix(g)},${mix(b)})`;
}

// ── Provider monogram badge (small brand-ish letter chip on known destinations) ──
const PROVIDER_BADGE: Record<string, { letter: string; color: string }> = {
  openai: { letter: 'O', color: '#10a37f' },
  'azure openai': { letter: 'Az', color: '#0078d4' },
  anthropic: { letter: 'A', color: '#d97757' },
  'google gemini': { letter: 'G', color: '#4285f4' },
  'google vertex': { letter: 'V', color: '#4285f4' },
  'aws bedrock': { letter: 'B', color: '#ff9900' },
  cohere: { letter: 'C', color: '#39594d' },
  mistral: { letter: 'M', color: '#ff7000' },
  groq: { letter: 'Gq', color: '#f55036' },
  openrouter: { letter: 'OR', color: '#6467f2' },
  huggingface: { letter: 'HF', color: '#ffb02e' },
  perplexity: { letter: 'P', color: '#20808d' },
  together: { letter: 'T', color: '#0f6fff' },
  deepseek: { letter: 'D', color: '#4d6bfe' },
  portkey: { letter: 'PK', color: '#e2e8f0' },
  'cloudflare ai gateway': { letter: 'CF', color: '#f38020' },
};
function providerBadge(provider?: string): { letter: string; color: string } | null {
  if (!provider) return null;
  const known = PROVIDER_BADGE[provider.toLowerCase()];
  if (known) return known;
  const letter = provider.trim().slice(0, 1).toUpperCase();
  if (!letter) return null;
  // Deterministic fallback color from the provider name so unlisted providers
  // still get a stable, distinct badge instead of a generic gray.
  let hash = 0;
  for (let i = 0; i < provider.length; i++) hash = (hash * 31 + provider.charCodeAt(i)) >>> 0;
  const hue = hash % 360;
  return { letter, color: `hsl(${hue}, 65%, 55%)` };
}

// ── Hand-drawn vector glyphs (crisp at any zoom, no image loading) ───────────
function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Draws the node's type glyph centered at (0,0) in a scale of `s`. Caller translates first. */
function drawGlyph(ctx: CanvasRenderingContext2D, type: NodeType, s: number, color: string) {
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (type === 'identity') {
    // Head + shoulders — a minimal "person" glyph.
    ctx.beginPath();
    ctx.arc(0, -s * 0.32, s * 0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(-s * 0.55, s * 0.5);
    ctx.quadraticCurveTo(0, -s * 0.02, s * 0.55, s * 0.5);
    ctx.quadraticCurveTo(0, s * 0.68, -s * 0.55, s * 0.5);
    ctx.fill();
    return;
  }

  if (type === 'agent') {
    // Chip/CPU glyph — rounded body, pins, inner core.
    const b = s * 0.55;
    roundRectPath(ctx, -b, -b, b * 2, b * 2, s * 0.16);
    ctx.fill();
    ctx.lineWidth = Math.max(s * 0.1, 0.5);
    const offsets = [-0.5, 0, 0.5];
    for (const p of offsets) {
      const at = p * b * 1.5;
      ctx.beginPath();
      ctx.moveTo(at, -b);
      ctx.lineTo(at, -b - s * 0.2);
      ctx.moveTo(at, b);
      ctx.lineTo(at, b + s * 0.2);
      ctx.moveTo(-b, at);
      ctx.lineTo(-b - s * 0.2, at);
      ctx.moveTo(b, at);
      ctx.lineTo(b + s * 0.2, at);
      ctx.stroke();
    }
    ctx.lineWidth = Math.max(s * 0.1, 0.5);
    roundRectPath(ctx, -b * 0.42, -b * 0.42, b * 0.84, b * 0.84, s * 0.06);
    ctx.stroke();
    return;
  }

  if (type === 'destination') {
    // Lightning bolt — matches the "AI Endpoints" metric icon elsewhere on the page.
    const w = s * 0.95;
    ctx.beginPath();
    ctx.moveTo(w * 0.12, -w * 0.62);
    ctx.lineTo(-w * 0.38, w * 0.05);
    ctx.lineTo(-w * 0.06, w * 0.05);
    ctx.lineTo(-w * 0.22, w * 0.62);
    ctx.lineTo(w * 0.42, -w * 0.06);
    ctx.lineTo(w * 0.1, -w * 0.06);
    ctx.closePath();
    ctx.fill();
    return;
  }

  // mcp — server rack: outlined card + rack lines + status LEDs.
  const w = s * 1.15;
  const h = s * 1.15;
  ctx.lineWidth = Math.max(s * 0.09, 0.5);
  roundRectPath(ctx, -w / 2, -h / 2, w, h, s * 0.16);
  ctx.stroke();
  for (const yFrac of [-0.28, 0.05, 0.38]) {
    const y = yFrac * h;
    ctx.beginPath();
    ctx.moveTo(-w / 2 + s * 0.18, y);
    ctx.lineTo(w / 2 - s * 0.32, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(w / 2 - s * 0.16, y, s * 0.05, 0, Math.PI * 2);
    ctx.fill();
  }
}

// ── Flow layout ──────────────────────────────────────────────────────────────
// The force ("cluster") layout answers "what clumps together"; it does not
// answer the question this page actually exists to answer, which is
// "who → what → where". Flow mode lays the same graph out as three left-to-right
// bands (identities, agents, destinations), each band wrapped into as many
// sub-columns as it needs so a busy environment stays roughly square instead of
// becoming a 40-node-tall strip.
type BandKey = 'identity' | 'agent' | 'dest';
const BAND_ORDER: BandKey[] = ['identity', 'agent', 'dest'];
const BAND_TITLE: Record<BandKey, string> = {
  identity: 'IDENTITIES',
  agent: 'AGENTS',
  dest: 'AI DESTINATIONS & MCP',
};
const SUB_GAP = 215; // px between wrapped sub-columns inside one band
const BAND_GAP = 330; // px of clear space between bands
const ROW_GAP = 54; // vertical pitch inside a column
const MAX_ROWS = 14; // wrap a band into another sub-column past this many nodes

const bandOf = (t: NodeType): BandKey => (t === 'identity' ? 'identity' : t === 'agent' ? 'agent' : 'dest');

interface FlowLayout {
  targets: Map<string, { x: number; y: number }>;
  headers: { x: number; title: string; count: number }[];
}

function computeFlowLayout(
  nodes: GraphNode[],
  adjacency: Map<string, Set<string>>,
  degree: Map<string, number>
): FlowLayout {
  const groups: Record<BandKey, GraphNode[]> = { identity: [], agent: [], dest: [] };
  for (const n of nodes) groups[bandOf(n.type)].push(n);

  const byDegreeThenName = (a: GraphNode, b: GraphNode) => {
    const d = (degree.get(b.id) || 0) - (degree.get(a.id) || 0);
    return d !== 0 ? d : (a.label || '').localeCompare(b.label || '');
  };

  // Order each band relative to the one before it (a barycentre pass, the same
  // idea layered graph drawing uses): a node sits next to whichever upstream
  // node it hangs off, which is what keeps the edge bundles from turning into
  // a cat's cradle. Risk breaks ties inside a bundle so the worst destination
  // of each agent still floats to the top of that agent's block.
  groups.identity.sort(byDegreeThenName);
  const rank = new Map<string, number>();
  groups.identity.forEach((n, i) => rank.set(n.id, i));

  const orderAgainstPrevious = (list: GraphNode[]) => {
    const anchor = (n: GraphNode): number => {
      let best = Number.POSITIVE_INFINITY;
      for (const nb of adjacency.get(n.id) || []) {
        const r = rank.get(nb);
        if (r !== undefined && r < best) best = r;
      }
      return best;
    };
    list.sort((a, b) => {
      const aa = anchor(a);
      const ab = anchor(b);
      if (aa !== ab) return aa - ab;
      const ra = RISK_RANK[a.risk || ''] ?? 9;
      const rb = RISK_RANK[b.risk || ''] ?? 9;
      if (ra !== rb) return ra - rb;
      return byDegreeThenName(a, b);
    });
    list.forEach((n, i) => rank.set(n.id, i));
  };

  orderAgainstPrevious(groups.agent);
  orderAgainstPrevious(groups.dest);

  // Lay the bands out left-to-right, each as wide as its own wrap requires.
  const cols: Record<BandKey, number> = { identity: 1, agent: 1, dest: 1 };
  for (const key of BAND_ORDER) cols[key] = Math.max(1, Math.ceil(groups[key].length / MAX_ROWS));

  const centers: Record<BandKey, number> = { identity: 0, agent: 0, dest: 0 };
  let cursor = 0;
  for (const key of BAND_ORDER) {
    const w = (cols[key] - 1) * SUB_GAP;
    centers[key] = cursor + w / 2;
    cursor += w + BAND_GAP;
  }
  const shift = (cursor - BAND_GAP) / 2; // recentre the whole diagram on x=0
  for (const key of BAND_ORDER) centers[key] -= shift;

  const targets = new Map<string, { x: number; y: number }>();
  for (const key of BAND_ORDER) {
    const list = groups[key];
    if (!list.length) continue;
    const nCols = cols[key];
    const rows = Math.ceil(list.length / nCols);
    list.forEach((n, i) => {
      const c = Math.floor(i / rows);
      const r = i % rows;
      targets.set(n.id, {
        x: centers[key] + (c - (nCols - 1) / 2) * SUB_GAP,
        y: (r - (rows - 1) / 2) * ROW_GAP,
      });
    });
  }

  const headers = BAND_ORDER.filter((k) => groups[k].length > 0).map((k) => ({
    x: centers[k],
    title: BAND_TITLE[k],
    count: groups[k].length,
  }));

  return { targets, headers };
}

const ShadowAiGraph: React.FC<Props> = ({ apiUrl }) => {
  const { t: tr } = useTranslation();
  const [data, setData] = useState<DiscoveryGraph | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sources, setSources] = useState<string[]>([]);
  const [risks, setRisks] = useState<RiskLevel[]>([]);
  const [types, setTypes] = useState<NodeType[]>([]);
  const [managed, setManaged] = useState<'all' | 'shadow' | 'managed'>('all');
  const [timeRangeIdx, setTimeRangeIdx] = useState(TIME_RANGE_PRESETS.length - 1); // default: All time

  const [layout, setLayout] = useState<'flow' | 'cluster'>('flow');
  const [query, setQuery] = useState('');
  const [showLabels, setShowLabels] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [selected, setSelected] = useState<GraphNode | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [hovered, setHovered] = useState<GraphNode | null>(null);
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null);
  const [legendOpen, setLegendOpen] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null); // draft name while editing
  const [renameError, setRenameError] = useState<string | null>(null);

  const [width, setWidth] = useState(800);
  const [canvasHeight, setCanvasHeight] = useState(640);

  const containerRef = useRef<HTMLDivElement | null>(null); // fullscreen target (card + filters + canvas)
  const filterBarRef = useRef<HTMLDivElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null); // canvas wrapper (width/hover measurement)
  const filtersBtnRef = useRef<HTMLDivElement | null>(null);
  const fgRef = useRef<any>(null);
  // Auto-fit exactly once per data/layout change, so a later engine settle can't
  // yank the viewport back after the user has panned or zoomed.
  const fittedRef = useRef(false);
  // Labels are queued during the node pass and flushed after every node/link has
  // been drawn, so a label is never painted over by a neighbouring node.
  const labelQueueRef = useRef<
    { x: number; y: number; text: string; alpha: number; priority: number; accent: string | null }[]
  >([]);

  const timeRangeMs = TIME_RANGE_PRESETS[timeRangeIdx].ms;
  const filters: GraphFilters = useMemo(
    () => ({
      sources,
      risks,
      types,
      managed,
      since: timeRangeMs ? new Date(Date.now() - timeRangeMs).toISOString() : undefined,
    }),
    [sources, risks, types, managed, timeRangeMs]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getDiscoveryGraph(apiUrl, filters));
    } catch (err: any) {
      setError(err.message || 'Failed to load graph');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, filters]);

  useEffect(() => {
    load();
  }, [load]);

  // Track container width (and, in fullscreen, available height) so the canvas
  // fills the space responsively. A ResizeObserver on the wrapper (rather than
  // only a window 'resize' listener) catches every reason this element's own
  // size can change — sidebar collapse, tab switches, fullscreen toggle — not
  // just the browser window resizing, and re-measures immediately on mount
  // instead of only after the next resize event.
  useEffect(() => {
    const measure = () => {
      if (wrapRef.current) setWidth(wrapRef.current.clientWidth);
      const filterH = filterBarRef.current?.offsetHeight || 0;
      if (isFullscreen) {
        setCanvasHeight(window.innerHeight - filterH);
      } else {
        // A generous share of the window rather than a fixed 560, which turned
        // the graph into a letterbox slot you had to scroll inside of. Derived
        // from the window (not from this element's own position on the page) so
        // resizing the canvas can't feed back into its own measurement.
        setCanvasHeight(Math.max(560, Math.round(window.innerHeight * 0.72)));
      }
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = wrapRef.current ? new ResizeObserver(measure) : null;
    if (ro && wrapRef.current) ro.observe(wrapRef.current);
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [isFullscreen]);

  // Fullscreen toggle — fullscreens the whole card (filters + legend + canvas
  // stay usable), synced to the real browser fullscreen state so Esc works too.
  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement === containerRef.current) {
      document.exitFullscreen();
    } else {
      containerRef.current?.requestFullscreen();
    }
  };

  // Close the filter popover on an outside click.
  useEffect(() => {
    if (!filtersOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!filtersBtnRef.current?.contains(e.target as Node)) setFiltersOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [filtersOpen]);

  // Cursor-following tooltip position — only tracked while a node is hovered,
  // so this doesn't churn re-renders during idle mouse movement.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !hovered) return;
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      setHoverPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, [hovered]);

  // ── Derived graph state ────────────────────────────────────────────────────

  // Adjacency over the *unfiltered* result, so focus mode and the neighbour list
  // agree regardless of what is currently drawn.
  const adjacency = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const l of data?.links || []) {
      const s = typeof l.source === 'object' ? (l.source as any).id : l.source;
      const t = typeof l.target === 'object' ? (l.target as any).id : l.target;
      if (!m.has(s)) m.set(s, new Set());
      if (!m.has(t)) m.set(t, new Set());
      m.get(s)!.add(t);
      m.get(t)!.add(s);
    }
    return m;
  }, [data]);

  const degree = useMemo(() => {
    const m = new Map<string, number>();
    for (const [id, set] of adjacency) m.set(id, set.size);
    return m;
  }, [adjacency]);

  // The node/link set actually on screen. Focus mode narrows the same data down
  // to one node's immediate neighbourhood — the practical way out of a hairball
  // without touching the filters.
  const visible = useMemo(() => {
    const allNodes = data?.nodes || [];
    const allLinks = data?.links || [];
    if (!focusId) return { nodes: allNodes, links: allLinks };
    const keep = new Set<string>([focusId, ...Array.from(adjacency.get(focusId) || [])]);
    return {
      nodes: allNodes.filter((n) => keep.has(n.id)),
      links: allLinks.filter((l) => {
        const s = typeof l.source === 'object' ? (l.source as any).id : l.source;
        const t = typeof l.target === 'object' ? (l.target as any).id : l.target;
        return keep.has(s) && keep.has(t);
      }),
    };
  }, [data, focusId, adjacency]);

  const flowLayout = useMemo(
    () => computeFlowLayout(visible.nodes as GraphNode[], adjacency, degree),
    [visible.nodes, adjacency, degree]
  );

  // ForceGraph2D mutates node objects (x/y/vx/vy); hand it fresh copies each load.
  // In flow mode the slots are also written straight onto the nodes as fixed
  // positions: this view is a diagram, not a physics toy, so it should come up
  // already laid out instead of animating its way out of a random blob.
  const graphData = useMemo(() => {
    const nodes = visible.nodes.map((n) => {
      const copy: any = { ...n };
      const t = layout === 'flow' ? flowLayout.targets.get(n.id) : undefined;
      if (t) {
        copy.x = copy.fx = t.x;
        copy.y = copy.fy = t.y;
      }
      return copy;
    });
    return { nodes, links: visible.links.map((l) => ({ ...l })) };
  }, [visible, layout, flowLayout]);

  // Cluster mode keeps the classic repulsion layout, scaled by node count — a
  // spacing that reads fine at 20 nodes packs into an unreadable blob at 200.
  // Flow mode needs no force tuning at all: its nodes carry fx/fy, and d3 skips
  // the integration step for pinned nodes whatever the forces say.
  useEffect(() => {
    const fg = fgRef.current;
    const nodeCount = graphData.nodes.length;
    if (!fg || !nodeCount || layout !== 'cluster') return;

    const chargeStrength = nodeCount > 150 ? -420 : nodeCount > 60 ? -320 : -240;
    const linkDistance = nodeCount > 150 ? 95 : nodeCount > 60 ? 80 : 70;
    const collidePad = nodeCount > 150 ? 26 : nodeCount > 60 ? 22 : 18;
    fg.d3Force('charge')?.strength(chargeStrength).distanceMax(600);
    fg.d3Force('link')?.distance(linkDistance);
    fg.d3Force('collide', forceCollide((n: any) => nodeRadius(n) + collidePad));

    fittedRef.current = false;
    fg.d3ReheatSimulation();
  }, [graphData, layout]);

  const nodeById = useMemo(() => {
    const m = new Map<string, GraphNode>();
    for (const n of graphData.nodes) m.set(n.id, n as GraphNode);
    return m;
  }, [graphData.nodes]);

  const neighbours = useMemo(
    () => (selected ? adjacency.get(selected.id) || new Set<string>() : new Set<string>()),
    [selected, adjacency]
  );

  const neighbourNodes = useMemo(
    () =>
      Array.from(neighbours)
        .map((id) => nodeById.get(id))
        .filter(Boolean) as GraphNode[],
    [neighbours, nodeById]
  );

  // Search matches — highlighted, never hidden, so you keep the surrounding
  // context instead of losing it the moment you type.
  const matchIds = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const s = new Set<string>();
    for (const n of graphData.nodes) {
      const hay = `${n.label} ${n.provider || ''} ${n.type} ${n.risk || ''} ${n.source}`.toLowerCase();
      if (hay.includes(q)) s.add(n.id);
    }
    return s;
  }, [query, graphData.nodes]);

  // A search hit on its own tells you nothing — "Iphone" matters because of what
  // it reaches. Matches plus their direct neighbours stay legible; the rest of
  // the graph recedes.
  const searchContext = useMemo(() => {
    if (!matchIds) return null;
    const s = new Set<string>(matchIds);
    for (const id of Array.from(matchIds)) {
      for (const nb of adjacency.get(id) || []) s.add(nb);
    }
    return s;
  }, [matchIds, adjacency]);

  const stats = useMemo(() => {
    const s = { identity: 0, agent: 0, dest: 0, risky: 0 };
    for (const n of graphData.nodes) {
      if (n.type === 'identity') s.identity++;
      else if (n.type === 'agent') s.agent++;
      else s.dest++;
      if (n.risk === 'high_risk' || n.risk === 'unsanctioned') s.risky++;
    }
    return s;
  }, [graphData.nodes]);

  // ── Camera helpers ─────────────────────────────────────────────────────────
  const fit = useCallback(() => {
    fgRef.current?.zoomToFit(500, 70);
  }, []);

  // Flow mode knows every position up front, so frame it as soon as it is drawn.
  // (Cluster mode has to wait for the simulation to settle — that fit is driven
  // by onEngineStop instead.)
  useEffect(() => {
    if (layout !== 'flow' || !graphData.nodes.length) return;
    const t = setTimeout(() => {
      fittedRef.current = true;
      fgRef.current?.zoomToFit(400, 70);
    }, 60);
    return () => clearTimeout(t);
  }, [graphData, layout, width, canvasHeight]);
  const zoomBy = useCallback((factor: number) => {
    const fg = fgRef.current;
    if (!fg) return;
    fg.zoom(Math.min(8, Math.max(0.15, fg.zoom() * factor)), 220);
  }, []);

  const focusNode = useCallback((n: GraphNode) => {
    setSelected(n);
    const fg = fgRef.current;
    const live = n as GraphNode & { x?: number; y?: number };
    if (fg && Number.isFinite(live.x) && Number.isFinite(live.y)) {
      // Nudge left so the node isn't parked under the detail panel.
      fg.centerAt(live.x! + PANEL_WIDTH / 2 / Math.max(fg.zoom(), 0.2), live.y!, 500);
    }
  }, []);

  // Agents are keyed by a discovery hash; this is how an operator replaces that
  // with a name they recognise, from the place they actually noticed the problem.
  const saveRename = useCallback(async () => {
    if (!selected?.discId || renaming === null) return;
    setRenameError(null);
    try {
      await renameDiscoveredAgent(apiUrl, selected.discId, renaming);
      setRenaming(null);
      await load();
      setSelected(null);
    } catch (err: any) {
      setRenameError(err.message || 'Rename failed');
    }
  }, [apiUrl, selected, renaming, load]);

  // Enter jumps to the first search hit; Esc backs out of whatever is focused.
  const jumpToFirstMatch = useCallback(() => {
    if (!matchIds || !matchIds.size) return;
    const first = graphData.nodes.find((n) => matchIds.has(n.id));
    if (first) focusNode(first as GraphNode);
  }, [matchIds, graphData.nodes, focusNode]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (selected) setSelected(null);
      else if (focusId) setFocusId(null);
      else if (query) setQuery('');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selected, focusId, query]);

  // ── Link styling ───────────────────────────────────────────────────────────
  const endpointNode = (ref: any): (GraphNode & { x?: number; y?: number }) | undefined =>
    typeof ref === 'object' ? ref : nodeById.get(ref);

  /** Baseline link color: risk-tinted for egress edges, muted violet for identity->agent. */
  const linkBaseColor = (l: any): string => {
    const target = endpointNode(l.target);
    if (l.kind === 'egress' && target?.risk) return RISK_COLOR[target.risk];
    if (target?.type === 'mcp') return TYPE_COLOR.mcp;
    return TYPE_COLOR.identity;
  };

  const isRiskyEgress = (l: any): boolean => {
    const target = endpointNode(l.target);
    return l.kind === 'egress' && (target?.risk === 'unsanctioned' || target?.risk === 'high_risk');
  };

  /**
   * How prominently something draws, given whatever is currently emphasised.
   * A single scheme for nodes and links: when only the nodes were dimmed (the
   * old behaviour for search), every edge kept full brightness and the graph
   * filled up with bright lines hanging off invisible endpoints.
   *
   *   'lead'    — the thing you asked about
   *   'context' — its immediate neighbourhood, readable but subordinate
   *   'mute'    — everything else, barely there
   *   'normal'  — nothing is emphasised, draw as usual
   */
  type Emphasis = 'lead' | 'context' | 'mute' | 'normal';

  const nodeEmphasis = (id: string): Emphasis => {
    if (selected) {
      if (id === selected.id) return 'lead';
      return neighbours.has(id) ? 'context' : 'mute';
    }
    if (matchIds && searchContext) {
      if (matchIds.has(id)) return 'lead';
      return searchContext.has(id) ? 'context' : 'mute';
    }
    return 'normal';
  };

  /** A link leads if it touches the emphasised node, and is context if it joins
   *  two nodes that are both still in view. */
  const linkEmphasis = (l: any): Emphasis => {
    if (!selected && !matchIds) return 'normal';
    const s = endpointNode(l.source)?.id ?? l.source;
    const t = endpointNode(l.target)?.id ?? l.target;
    const es = nodeEmphasis(s);
    const et = nodeEmphasis(t);
    if (es === 'lead' || et === 'lead') return 'lead';
    if (es === 'mute' || et === 'mute') return 'mute';
    return 'context';
  };

  // ── Filter chip plumbing ───────────────────────────────────────────────────
  const toggle = <T,>(list: T[], v: T, set: (x: T[]) => void) =>
    set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const chip = (active: boolean): React.CSSProperties => ({
    padding: '3px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    border: `1px solid ${active ? 'var(--info)' : 'var(--border)'}`,
    background: active ? 'color-mix(in srgb, var(--info) 15%, transparent)' : 'var(--bg)',
    color: active ? 'var(--info)' : 'var(--text-2)',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  });

  const available = data?.available || { sources: [], risks: [], types: [] };
  const activeFilterCount = types.length + risks.length + sources.length;
  const hasData = graphData.nodes.length > 0;
  const graphWidth = width;

  const clearAllFilters = () => {
    setTypes([]);
    setRisks([]);
    setSources([]);
  };

  return (
    <div ref={containerRef} className="ent-card" style={{ padding: 0, position: 'relative' }}>
      {/* ── Header: toolbar + legend + focus banner ──────────────────────────── */}
      <div ref={filterBarRef}>
      <div
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center',
        }}
      >
        {/* Search */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <span style={{ position: 'absolute', left: 8, display: 'flex', color: 'var(--text-2)', pointerEvents: 'none' }}>
            <Icon name="search" size={13} />
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && jumpToFirstMatch()}
            placeholder={tr('components.shadowAiGraph.searchHostAgentProvider')}
            style={{
              width: 220,
              padding: '5px 26px 5px 27px',
              fontSize: 12,
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'var(--bg)',
              color: 'var(--text)',
              outline: 'none',
            }}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              title={tr('components.shadowAiGraph.clearSearch')}
              style={{
                position: 'absolute',
                right: 5,
                display: 'flex',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-2)',
                padding: 2,
              }}
            >
              <Icon name="close" size={12} />
            </button>
          )}
        </div>
        {matchIds && (
          <span style={{ fontSize: 11, color: matchIds.size ? 'var(--info)' : 'var(--text-2)', fontWeight: 600 }}>
            {tr('components.shadowAiGraph.matchCount', { count: matchIds.size })}
          </span>
        )}

        {/* Layout */}
        <Segmented
          options={[
            { value: 'flow', labelKey: 'components.shadowAiGraph.flow', titleKey: 'components.shadowAiGraph.flowTitle' },
            { value: 'cluster', labelKey: 'components.shadowAiGraph.cluster', titleKey: 'components.shadowAiGraph.clusterTitle' },
          ]}
          value={layout}
          onChange={(v) => setLayout(v as 'flow' | 'cluster')}
        />

        {/* Scope */}
        <Segmented
          options={[
            { value: 'all', labelKey: 'components.shadowAiGraph.scopeAll' },
            { value: 'shadow', labelKey: 'components.shadowAiGraph.scopeShadow' },
            { value: 'managed', labelKey: 'components.shadowAiGraph.scopeManaged' },
          ]}
          value={managed}
          onChange={(v) => setManaged(v as 'all' | 'shadow' | 'managed')}
        />

        {/* Time range */}
        <select
          value={timeRangeIdx}
          onChange={(e) => setTimeRangeIdx(Number(e.target.value))}
          title={tr('components.shadowAiGraph.onlyShowActivityObservedInThis')}
          style={{
            padding: '5px 8px',
            fontSize: 12,
            borderRadius: 6,
            border: '1px solid var(--border)',
            background: 'var(--bg)',
            color: 'var(--text)',
            cursor: 'pointer',
          }}
        >
          {TIME_RANGE_PRESETS.map((p, i) => (
            <option key={p.labelKey} value={i}>
              {tr(p.labelKey)}
            </option>
          ))}
        </select>

        {/* Filters popover */}
        <div ref={filtersBtnRef} style={{ position: 'relative' }}>
          <button className="ent-btn sm" onClick={() => setFiltersOpen((v) => !v)}>
            {tr('components.shadowAiGraph.filters')}
            {activeFilterCount > 0 && (
              <span
                style={{
                  marginLeft: 2,
                  background: 'var(--info)',
                  color: '#fff',
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '0 5px',
                }}
              >
                {activeFilterCount}
              </span>
            )}
            <Icon name="chevronDown" size={11} />
          </button>
          {filtersOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: 6,
                zIndex: 20,
                width: 300,
                background: 'var(--bg-2)',
                border: '1px solid var(--border-strong)',
                borderRadius: 8,
                boxShadow: '0 8px 28px rgba(0,0,0,0.28)',
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <FilterBlock label={tr('components.shadowAiGraph.nodeType')} items={available.types} render={(t) => TYPE_LABEL[t] || t} selected={types} onToggle={(t) => toggle(types, t, setTypes)} chip={chip} />
              <FilterBlock label={tr('components.shadowAiGraph.risk')} items={available.risks as RiskLevel[]} render={(r) => RISK_LABEL[r] || r} selected={risks} onToggle={(r) => toggle(risks, r, setRisks)} chip={chip} />
              <FilterBlock label={tr('components.shadowAiGraph.discoverySource')} items={available.sources} render={(s) => s} selected={sources} onToggle={(s) => toggle(sources, s, setSources)} chip={chip} />
              <button className="ent-btn sm" onClick={clearAllFilters} disabled={!activeFilterCount}>
                {tr('components.shadowAiGraph.clearAllFilters')}
              </button>
            </div>
          )}
        </div>

        {/* Right-hand cluster: what you're looking at + camera controls */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
            {tr('components.shadowAiGraph.graphStats', { identities: stats.identity, agents: stats.agent, destinations: stats.dest })}
            {stats.risky > 0 && (
              <>
                {' · '}
                <strong style={{ color: RISK_COLOR.unsanctioned }}>{tr('components.shadowAiGraph.risky', { v0: stats.risky })}</strong>
              </>
            )}
          </span>
          <button className="ent-btn sm" onClick={() => setShowLabels((v) => !v)} title={tr('components.shadowAiGraph.showOrHideNodeLabels')}>
            {showLabels ? tr('components.shadowAiGraph.hideLabels') : tr('components.shadowAiGraph.showLabels')}
          </button>
          <div style={{ display: 'flex' }}>
            <button className="ent-btn sm" onClick={() => zoomBy(1 / 1.4)} title={tr('components.shadowAiGraph.zoomOut')} style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}>
              −
            </button>
            <button className="ent-btn sm" onClick={() => zoomBy(1.4)} title={tr('components.shadowAiGraph.zoomIn')} style={{ borderRadius: 0, marginLeft: -1 }}>
              +
            </button>
            <button className="ent-btn sm" onClick={fit} title={tr('components.shadowAiGraph.fitGraphToView')} style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, marginLeft: -1 }}>
              {tr('components.shadowAiGraph.fit')}
            </button>
          </div>
          <button className="ent-btn sm" onClick={load} disabled={loading} title={tr('components.shadowAiGraph.reloadTheGraph')}>
            <Icon name="refresh" className={loading ? 'spin' : ''} />
          </button>
          <button className="ent-btn sm" onClick={toggleFullscreen} title={isFullscreen ? tr('components.shadowAiGraph.exitFullScreen') : tr('components.shadowAiGraph.fullScreen')}>
            <Icon name={isFullscreen ? 'collapse' : 'expand'} />
          </button>
        </div>
      </div>

      {/* Legend — a single always-visible strip under the toolbar. It used to be
          collapsed behind a button (so the colour coding was undecipherable by
          default) and an overlay on the canvas is no better: it sits on top of
          the very nodes you are trying to read. */}
      <div
        style={{
          padding: legendOpen ? '7px 14px' : '4px 14px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 14,
          fontSize: 10.5,
          color: 'var(--text-2)',
        }}
      >
        <button
          onClick={() => setLegendOpen((v) => !v)}
          title={legendOpen ? tr('components.shadowAiGraph.hideLegend') : tr('components.shadowAiGraph.showLegend')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: 'none',
            border: 'none',
            color: 'var(--text-2)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <Icon name="info" size={11} /> {tr('components.shadowAiGraph.legend')}
          <Icon name={legendOpen ? 'chevronDown' : 'chevronRight'} size={10} />
        </button>
        {legendOpen && (
          <>
            <LegendRow icon="users" color={TYPE_COLOR.identity} label={tr('components.shadowAiGraph.identity')} />
            <LegendRow icon="chip" color={TYPE_COLOR.agent} label={tr('components.shadowAiGraph.agent')} />
            <LegendRow icon="bolt" color={RISK_COLOR.sanctioned} label={tr('components.shadowAiGraph.sanctioned')} />
            <LegendRow icon="bolt" color={RISK_COLOR.tolerated} label={tr('components.shadowAiGraph.tolerated')} />
            <LegendRow icon="bolt" color={RISK_COLOR.unsanctioned} label={tr('components.shadowAiGraph.unsanctionedHighRisk')} />
            <LegendRow icon="server" color={TYPE_COLOR.mcp} label={tr('components.shadowAiGraph.mcpServer')} />
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--text-2)', flexShrink: 0 }} />
              {tr('components.shadowAiGraph.solidManaged')}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid var(--text-2)', flexShrink: 0 }} />
              {tr('components.shadowAiGraph.ringedShadow')}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 16, height: 6, borderRadius: 3, background: 'linear-gradient(90deg, #ef4444, transparent)', flexShrink: 0 }} />
              {tr('components.shadowAiGraph.movingLineRiskyEgress')}
            </span>
            <span style={{ marginLeft: 'auto', color: 'var(--text-3, var(--text-2))', opacity: 0.8 }}>
              {tr('components.shadowAiGraph.clickANodeForDetailsDrag')}
            </span>
          </>
        )}
      </div>

      {/* Focus-mode banner — makes it obvious you're not looking at everything. */}
      {focusId && (
        <div
          style={{
            padding: '7px 14px',
            borderBottom: '1px solid var(--border)',
            background: 'color-mix(in srgb, var(--info) 10%, transparent)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 12,
            color: 'var(--text)',
          }}
        >
          <Icon name="view" size={13} />
          {tr('components.shadowAiGraph.focusedOn')} <strong>{shortLabel(nodeById.get(focusId) || ({ label: focusId } as GraphNode))}</strong> {tr('components.shadowAiGraph.andItsDirectConnections')}
          <button className="ent-btn sm" style={{ marginLeft: 'auto' }} onClick={() => setFocusId(null)}>
            {tr('components.shadowAiGraph.showWholeGraph')}
          </button>
        </div>
      )}
      </div>

      {/* ── Canvas ──────────────────────────────────────────────────────────── */}
      <div
        ref={wrapRef}
        style={{
          position: 'relative',
          height: canvasHeight,
          background: 'radial-gradient(ellipse at 50% 30%, color-mix(in srgb, var(--info) 6%, var(--bg)) 0%, var(--bg) 70%)',
        }}
      >
        {error && <div className="ent-empty" style={{ padding: 40 }}>{error}</div>}

        {!error && !hasData && (
          <div className="ent-empty" style={{ padding: 60, textAlign: 'center', color: 'var(--text-2)' }}>
            <Icon name="search" size={28} />
            <p style={{ marginTop: 12, fontWeight: 600, color: 'var(--text)' }}>{tr('components.shadowAiGraph.noRelationshipsToShow')}</p>
            <p style={{ fontSize: 12, maxWidth: 420, margin: '6px auto 0' }}>
              {tr('components.shadowAiGraph.runAnAzureDiscoveryIngestLogs')}
            </p>
            {activeFilterCount > 0 && (
              <button className="ent-btn sm" style={{ marginTop: 12 }} onClick={clearAllFilters}>
                {tr('components.shadowAiGraph.clearFilters', { count: activeFilterCount })}
              </button>
            )}
          </div>
        )}

        {!error && hasData && (
          <ForceGraph2D
            ref={fgRef}
            graphData={graphData}
            width={graphWidth}
            height={canvasHeight}
            backgroundColor="rgba(0,0,0,0)"
            nodeRelSize={1}
            cooldownTicks={layout === 'flow' ? 0 : 120}
            linkCurvature={layout === 'flow' ? 0.06 : 0.14}
            minZoom={0.15}
            maxZoom={8}
            onEngineStop={() => {
              if (fittedRef.current) return;
              fittedRef.current = true;
              fgRef.current?.zoomToFit(400, 70);
            }}
            onNodeClick={(n: any) => {
              if (selected?.id === n.id) setSelected(null);
              else focusNode(n as GraphNode);
            }}
            onNodeHover={(n: any) => setHovered(n as GraphNode | null)}
            onNodeDragEnd={(n: any) => {
              // Pin where the user dropped it, so hand-arranged layouts stick.
              n.fx = n.x;
              n.fy = n.y;
            }}
            onBackgroundClick={() => setSelected(null)}
            linkColor={(l: any) => {
              const base = linkBaseColor(l);
              switch (linkEmphasis(l)) {
                case 'lead':
                  return rgba(base, 0.95);
                case 'context':
                  return rgba(base, 0.28);
                case 'mute':
                  return rgba(base, 0.04);
                default:
                  return rgba(base, isRiskyEgress(l) ? 0.5 : 0.22);
              }
            }}
            linkWidth={(l: any) => {
              switch (linkEmphasis(l)) {
                case 'lead':
                  return 2.5;
                case 'context':
                  return 1;
                case 'mute':
                  return 0.4;
                default:
                  return isRiskyEgress(l) ? 1.6 : 1;
              }
            }}
            linkDirectionalParticles={(l: any) => {
              const e = linkEmphasis(l);
              if (e === 'lead') return 3;
              if (e === 'normal') return isRiskyEgress(l) ? 2 : 0;
              return 0;
            }}
            linkDirectionalParticleWidth={(l: any) => (linkEmphasis(l) === 'lead' ? 3 : 2)}
            linkDirectionalParticleColor={(l: any) => linkBaseColor(l)}
            linkDirectionalParticleSpeed={0.004}
            nodePointerAreaPaint={(node: any, color: string, ctx: CanvasRenderingContext2D) => {
              const n = node as GraphNode & { x: number; y: number };
              if (!Number.isFinite(n.x) || !Number.isFinite(n.y)) return;
              ctx.fillStyle = color;
              ctx.beginPath();
              ctx.arc(n.x, n.y, nodeRadius(n) + 4, 0, 2 * Math.PI);
              ctx.fill();
            }}
            nodeCanvasObject={(node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
              const n = node as GraphNode & { x: number; y: number };
              // On the very first tick(s), before d3-force has assigned real
              // coordinates (esp. for isolated nodes), x/y can be NaN/undefined —
              // createRadialGradient throws on non-finite values, so skip this frame.
              if (!Number.isFinite(n.x) || !Number.isFinite(n.y)) return;
              const r = nodeRadius(n);
              const isMatch = matchIds ? matchIds.has(n.id) : false;
              const emphasis = nodeEmphasis(n.id);
              const dimmed = emphasis === 'mute';
              const color = nodeColor(n);
              const isRisky = n.risk === 'unsanctioned' || n.risk === 'high_risk';

              ctx.globalAlpha = emphasis === 'mute' ? 0.1 : emphasis === 'context' ? 0.62 : 1;

              // Soft outer glow for the risky destinations — the "this is the
              // thing to worry about" visual cue, cheap via shadowBlur.
              if (isRisky && !dimmed) {
                ctx.save();
                ctx.shadowColor = color;
                ctx.shadowBlur = n.risk === 'high_risk' ? 18 : 12;
                ctx.beginPath();
                ctx.arc(n.x, n.y, r, 0, 2 * Math.PI);
                ctx.fillStyle = color;
                ctx.fill();
                ctx.restore();
              }

              // Every node gets a filled disc so it reads at a glance; shadow
              // (unmanaged) nodes are additionally ringed in their own color on a
              // dark core, because in an all-shadow estate the old hollow-outline
              // treatment made the entire graph a set of faint wireframes.
              ctx.beginPath();
              ctx.arc(n.x, n.y, r, 0, 2 * Math.PI);
              if (n.managed) {
                const grad = ctx.createRadialGradient(n.x - r * 0.35, n.y - r * 0.35, r * 0.1, n.x, n.y, r * 1.1);
                grad.addColorStop(0, lighten(color, 0.4));
                grad.addColorStop(1, color);
                ctx.fillStyle = grad;
                ctx.fill();
              } else {
                ctx.fillStyle = rgba(color, 0.22);
                ctx.fill();
                ctx.lineWidth = Math.max(2.4 / globalScale, 1.2);
                ctx.strokeStyle = color;
                ctx.stroke();
                if (n.risk === 'high_risk') {
                  ctx.save();
                  ctx.setLineDash([2, 2]);
                  ctx.beginPath();
                  ctx.arc(n.x, n.y, r + 3, 0, 2 * Math.PI);
                  ctx.lineWidth = Math.max(1.2 / globalScale, 0.5);
                  ctx.stroke();
                  ctx.restore();
                }
              }

              // Search hit — amber halo, distinct from selection blue.
              if (isMatch) {
                ctx.lineWidth = Math.max(3 / globalScale, 1.4);
                ctx.strokeStyle = '#fbbf24';
                ctx.beginPath();
                ctx.arc(n.x, n.y, r + 4, 0, 2 * Math.PI);
                ctx.stroke();
              }

              // Selection ring.
              if (selected?.id === n.id) {
                ctx.lineWidth = Math.max(2.5 / globalScale, 1);
                ctx.strokeStyle = '#3b82f6';
                ctx.beginPath();
                ctx.arc(n.x, n.y, r + 2, 0, 2 * Math.PI);
                ctx.stroke();
              }

              // Hover ring — lighter-weight affordance distinct from selection.
              if (hovered?.id === n.id && selected?.id !== n.id) {
                ctx.lineWidth = Math.max(1.6 / globalScale, 0.8);
                ctx.strokeStyle = 'rgba(255,255,255,0.85)';
                ctx.beginPath();
                ctx.arc(n.x, n.y, r + 2, 0, 2 * Math.PI);
                ctx.stroke();
              }

              // Glyph: white on filled (managed) discs, colored on tinted (shadow) discs.
              ctx.save();
              ctx.translate(n.x, n.y);
              const glyphScale = Math.min(globalScale, 3); // keep glyphs crisp at deep zoom-out
              const s = r * 0.85 * Math.min(1, Math.max(0.55, glyphScale / 1.4));
              drawGlyph(ctx, n.type, s, n.managed ? '#ffffff' : color);
              ctx.restore();

              // Provider monogram badge (destinations/mcp only, when known).
              const badge = n.type === 'destination' || n.type === 'mcp' ? providerBadge(n.provider) : null;
              if (badge && !dimmed) {
                const br = Math.max(r * 0.42, 4.5);
                const bx = n.x + r * 0.72;
                const by = n.y + r * 0.72;
                ctx.beginPath();
                ctx.arc(bx, by, br, 0, 2 * Math.PI);
                ctx.fillStyle = badge.color;
                ctx.fill();
                ctx.lineWidth = 1 / globalScale;
                ctx.strokeStyle = 'rgba(0,0,0,0.35)';
                ctx.stroke();
                ctx.fillStyle = '#ffffff';
                ctx.font = `700 ${Math.max(br * 1.05, 5)}px -apple-system, system-ui, sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(badge.letter, bx, by + 0.5);
              }

              // Queue the label for the post-pass. Priority decides who wins when
              // two labels would collide: selection > hover > search hit > degree.
              const alwaysOn = selected?.id === n.id || hovered?.id === n.id || isMatch || neighbours.has(n.id);
              if ((showLabels || alwaysOn) && !dimmed) {
                let priority = degree.get(n.id) || 0;
                if (isMatch) priority += 1000;
                if (hovered?.id === n.id) priority += 5000;
                if (selected?.id === n.id) priority += 10000;
                labelQueueRef.current.push({
                  x: n.x,
                  y: n.y + r + 3,
                  text: shortLabel(n),
                  alpha: 1,
                  priority,
                  accent: isMatch ? '#fbbf24' : selected?.id === n.id ? '#3b82f6' : null,
                });
              }
              ctx.globalAlpha = 1;
            }}
            onRenderFramePost={(ctx: CanvasRenderingContext2D, globalScale: number) => {
              // Band headers in flow mode — without them the three columns are
              // just three columns; with them the picture explains itself.
              if (layout === 'flow' && fgRef.current) {
                const topLeft = fgRef.current.screen2GraphCoords(0, 0);
                const fs = 12 / globalScale;
                ctx.save();
                ctx.font = `800 ${fs}px -apple-system, system-ui, sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'top';
                ctx.fillStyle = 'rgba(148,163,184,0.75)';
                for (const h of flowLayout.headers) {
                  ctx.fillText(`${h.title} · ${h.count}`, h.x, topLeft.y + 10 / globalScale);
                }
                ctx.restore();
              }

              // Flush queued labels on top of everything, skipping any that would
              // land on top of one already drawn — overlapping label soup was the
              // main reason the old view was unreadable when zoomed out.
              const queue = labelQueueRef.current;
              labelQueueRef.current = [];
              if (!queue.length) return;
              queue.sort((a, b) => b.priority - a.priority);

              const fontSize = Math.max(11 / globalScale, 2.6);
              ctx.save();
              ctx.font = `600 ${fontSize}px -apple-system, system-ui, sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'top';
              const padX = fontSize * 0.45;
              const padY = fontSize * 0.3;
              const placed: { x1: number; y1: number; x2: number; y2: number }[] = [];

              for (const item of queue) {
                const tw = ctx.measureText(item.text).width;
                const box = {
                  x1: item.x - tw / 2 - padX,
                  y1: item.y,
                  x2: item.x + tw / 2 + padX,
                  y2: item.y + fontSize + padY * 2,
                };
                const clash = placed.some((p) => !(box.x2 < p.x1 || box.x1 > p.x2 || box.y2 < p.y1 || box.y1 > p.y2));
                if (clash) continue;
                placed.push(box);
                roundRectPath(ctx, box.x1, box.y1, box.x2 - box.x1, box.y2 - box.y1, fontSize * 0.35);
                ctx.fillStyle = 'rgba(12,15,22,0.82)';
                ctx.fill();
                if (item.accent) {
                  ctx.lineWidth = 1 / globalScale;
                  ctx.strokeStyle = item.accent;
                  ctx.stroke();
                }
                ctx.fillStyle = item.accent || '#e5e7eb';
                ctx.fillText(item.text, item.x, box.y1 + padY);
              }
              ctx.restore();
            }}
          />
        )}

        {/* Hover tooltip — follows the cursor while over a node. */}
        {hovered && hoverPos && (
          <div
            style={{
              position: 'absolute',
              left: Math.min(hoverPos.x + 16, Math.max(graphWidth - 250, 0)),
              top: Math.max(hoverPos.y - 10, 0),
              maxWidth: 240,
              background: 'rgba(15,18,25,0.94)',
              color: '#e5e7eb',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8,
              padding: '8px 10px',
              fontSize: 11,
              lineHeight: 1.5,
              pointerEvents: 'none',
              boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
              zIndex: 5,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, wordBreak: 'break-word' }}>
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: nodeColor(hovered),
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  flexShrink: 0,
                }}
              >
                <Icon name={TYPE_ICON[hovered.type]} size={8} />
              </span>
              {hovered.label}
            </div>
            <div style={{ marginTop: 4, color: '#9ca3af' }}>{TYPE_HINT[hovered.type]}</div>
            <div style={{ marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span>{hovered.managed ? tr('components.shadowAiGraph.managed') : tr('components.shadowAiGraph.shadow')}</span>
              {hovered.risk && <span>{RISK_LABEL[hovered.risk]}</span>}
              <span>{tr('components.shadowAiGraph.observed', { v0: hovered.count })}</span>
              <span>{tr('components.shadowAiGraph.connections', { v0: degree.get(hovered.id) || 0 })}</span>
            </div>
            <div style={{ marginTop: 4, color: '#64748b' }}>{tr('components.shadowAiGraph.clickForDetails')}</div>
          </div>
        )}

        {/* Selected-node detail — overlays the canvas rather than shrinking it,
            so opening the panel no longer re-lays-out the whole graph. */}
        {selected && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              width: PANEL_WIDTH,
              background: 'var(--bg-2)',
              borderLeft: '1px solid var(--border-strong)',
              boxShadow: '-4px 0 20px rgba(0,0,0,0.25)',
              padding: '16px',
              overflowY: 'auto',
              zIndex: 6,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  background: nodeColor(selected),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: '#fff',
                }}
              >
                <Icon name={TYPE_ICON[selected.type]} size={16} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {renaming !== null ? (
                  <input
                    autoFocus
                    value={renaming}
                    onChange={(e) => setRenaming(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveRename();
                      if (e.key === 'Escape') {
                        e.stopPropagation();
                        setRenaming(null);
                      }
                    }}
                    placeholder={tr('components.shadowAiGraph.nameThisAgent')}
                    maxLength={120}
                    style={{
                      width: '100%',
                      padding: '4px 6px',
                      fontSize: 12.5,
                      fontWeight: 600,
                      borderRadius: 5,
                      border: '1px solid var(--info)',
                      background: 'var(--bg)',
                      color: 'var(--text)',
                      outline: 'none',
                    }}
                  />
                ) : (
                  <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: 13, wordBreak: 'break-word' }}>{selected.label}</div>
                )}
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-2)', marginTop: 2 }}>
                  {TYPE_LABEL[selected.type]}
                </div>
              </div>
              <button className="ent-btn sm" style={{ padding: 4 }} onClick={() => setSelected(null)} title={tr('components.shadowAiGraph.closeEsc')}>
                <Icon name="close" size={14} />
              </button>
            </div>

            <p style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 10, lineHeight: 1.5 }}>{TYPE_HINT[selected.type]}</p>

            <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
              {renaming !== null ? (
                <>
                  <button className="ent-btn primary sm" onClick={saveRename}>
                    <Icon name="check" size={12} /> {tr('components.shadowAiGraph.saveName')}
                  </button>
                  <button className="ent-btn sm" onClick={() => { setRenaming(null); setRenameError(null); }}>
                    {tr('components.shadowAiGraph.cancel')}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="ent-btn sm"
                    onClick={() => setFocusId(focusId === selected.id ? null : selected.id)}
                    title={tr('components.shadowAiGraph.hideEverythingExceptThisNodeAnd')}
                  >
                    <Icon name="view" size={12} /> {focusId === selected.id ? tr('components.shadowAiGraph.unfocus') : tr('components.shadowAiGraph.focus')}
                  </button>
                  <button className="ent-btn sm" onClick={() => focusNode(selected)} title={tr('components.shadowAiGraph.centreTheViewOnThisNode')}>
                    {tr('components.shadowAiGraph.centre')}
                  </button>
                  {selected.discId && (
                    <button
                      className="ent-btn sm"
                      onClick={() => { setRenameError(null); setRenaming(selected.label); }}
                      title={tr('components.shadowAiGraph.giveThisAgentANameYou')}
                    >
                      {tr('components.shadowAiGraph.rename')}
                    </button>
                  )}
                </>
              )}
            </div>
            {renameError && (
              <div style={{ marginTop: 8, fontSize: 11, color: RISK_COLOR.unsanctioned }}>{renameError}</div>
            )}

            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
              <DetailRow label={tr('components.shadowAiGraph.status')} value={selected.managed ? `🛡 ${tr('components.shadowAiGraph.scopeManaged')}` : `⚠ ${tr('components.shadowAiGraph.scopeShadow')}`} />
              {selected.risk && <DetailRow label={tr('components.shadowAiGraph.risk')} value={RISK_LABEL[selected.risk]} valueColor={RISK_COLOR[selected.risk]} />}
              {selected.provider && <DetailRow label={tr('components.shadowAiGraph.provider')} value={selected.provider} />}
              <DetailRow label={tr('components.shadowAiGraph.source')} value={selected.source} />
              <DetailRow label={tr('components.shadowAiGraph.observed')} value={String(selected.count)} />
              <DetailRow label={tr('components.shadowAiGraph.connections')} value={String(neighbours.size)} />
            </div>

            {neighbourNodes.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-2)', marginBottom: 6 }}>{tr('components.shadowAiGraph.connectedTo', { v0: neighbourNodes.length })}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {neighbourNodes.map((nb) => (
                    <button
                      key={nb.id}
                      onClick={() => focusNode(nb)}
                      title={nb.label}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '6px 8px',
                        background: 'var(--bg)',
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        color: 'var(--text)',
                        fontSize: 11.5,
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          background: nodeColor(nb),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          flexShrink: 0,
                        }}
                      >
                        <Icon name={TYPE_ICON[nb.type]} size={9} />
                      </span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortLabel(nb)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Small presentational helpers ─────────────────────────────────────────────

const Segmented: React.FC<{
  options: { value: string; labelKey: string; titleKey?: string }[];
  value: string;
  onChange: (v: string) => void;
}> = ({ options, value, onChange }) => {
  const { t } = useTranslation();
  return (
  <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
    {options.map((o) => {
      const active = o.value === value;
      return (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          title={o.titleKey ? t(o.titleKey) : undefined}
          style={{
            padding: '5px 10px',
            fontSize: 11.5,
            fontWeight: 600,
            border: 'none',
            cursor: 'pointer',
            background: active ? 'var(--info)' : 'var(--bg)',
            color: active ? '#fff' : 'var(--text-2)',
          }}
        >
          {t(o.labelKey)}
        </button>
      );
    })}
  </div>
  );
};

function FilterBlock<T extends string>({
  label,
  items,
  render,
  selected,
  onToggle,
  chip,
}: {
  label: string;
  items: T[];
  render: (v: T) => string;
  selected: T[];
  onToggle: (v: T) => void;
  chip: (active: boolean) => React.CSSProperties;
}) {
  if (!items.length) return null;
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-2)', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        {items.map((v) => (
          <span key={v} style={chip(selected.includes(v))} onClick={() => onToggle(v)}>
            {render(v)}
          </span>
        ))}
      </div>
    </div>
  );
}

const LegendRow: React.FC<{ icon: 'users' | 'chip' | 'bolt' | 'server'; color: string; label: string }> = ({ icon, color, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <span
      style={{
        width: 15,
        height: 15,
        borderRadius: '50%',
        background: color,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
      }}
    >
      <Icon name={icon} size={9} />
    </span>
    {label}
  </div>
);

const DetailRow: React.FC<{ label: string; value: string; valueColor?: string }> = ({ label, value, valueColor }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
    <span style={{ color: 'var(--text-2)' }}>{label}</span>
    <span style={{ color: valueColor || 'var(--text)', fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
  </div>
);

export default ShadowAiGraph;
